import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlayIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useState } from 'react';
import {
  parseTicketEventName,
  type TicketEventType,
  type TicketHistoryMessage,
  type TicketMessageContent,
} from 'services/types/ticket';
import { BUBBLE_ADMIN, BUBBLE_ROLLBAR, BUBBLE_USER, NAVY, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

import MessageWrapper from './MessageWrapper';

const FONT = 'Jost, sans-serif';

const EVENT_ICON: Record<TicketEventType, typeof EditIcon> = {
  ticket_edited: EditIcon,
  ticket_analyzed_richard: AutoAwesomeIcon,
  admin_assigned: PersonAddIcon,
  ticket_reopened: RefreshIcon,
};

interface Props {
  message: TicketHistoryMessage;
  messageIndex: number;
  canEdit?: boolean;
  onEdit?: (messageIndex: number, content: string) => void;
}

type MessageTone = 'admin' | 'user' | 'rollbar' | 'ai' | 'event';

/** Names used by automatic/system-generated ticket messages. */
const SYSTEM_MESSAGE_NAMES = ['Rollbar Notification', 'Sistema de Auto-cierre'];

/** Derives the sender tone purely from the message — consistent for every viewer. */
const getMessageTone = (message: TicketHistoryMessage): MessageTone => {
  if (parseTicketEventName(message.name)) return 'event';
  if (message.role !== 'user' || !message.name) return 'ai';
  if (message.name.includes('(Admin)')) return 'admin';
  if (SYSTEM_MESSAGE_NAMES.includes(message.name)) return 'rollbar';
  return 'user';
};

const TONE_STYLE: Record<MessageTone, { align: 'left' | 'right' | 'center'; bgcolor?: string }> = {
  admin: { align: 'right', bgcolor: BUBBLE_ADMIN },
  user: { align: 'left', bgcolor: BUBBLE_USER },
  rollbar: { align: 'left', bgcolor: BUBBLE_ROLLBAR },
  ai: { align: 'left', bgcolor: undefined },
  event: { align: 'center', bgcolor: undefined },
};

const TicketMessageComponent: React.FC<Props> = ({ message, messageIndex, canEdit = false, onEdit = undefined }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  const eventMeta = parseTicketEventName(message.name);
  if (eventMeta) {
    const Icon = EVENT_ICON[eventMeta.type];
    const text = typeof message.content === 'string' ? message.content : '';
    const dateText = message.createdAt ? new Date(message.createdAt).toLocaleString('es-CL') : '';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 0.5 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_ITEM,
            px: 1.5,
            py: 0.4,
            color: alpha(NAVY, 0.6),
            fontFamily: FONT,
            fontSize: '0.7rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontWeight: 600,
          }}
        >
          <Icon sx={{ fontSize: '0.95rem' }} />
          <Box component="span">{text}</Box>
          {eventMeta.url && (
            <Link
              href={eventMeta.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: NAVY, fontWeight: 700, textTransform: 'uppercase', textDecoration: 'underline' }}
            >
              Ver sesión
            </Link>
          )}
          {dateText && (
            <Box component="span" sx={{ color: alpha(NAVY, 0.4) }}>
              {dateText}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  const getCaption = () => {
    if (message.name) {
      return message.name;
    }
    switch (message.role) {
      case 'user':
        return 'Usuario';
      case 'assistant':
        return 'Asistente';
      case 'tool':
        return 'Herramienta';
      default:
        return message.role;
    }
  };

  const handleEditMessage = () => {
    setEditedContent(message.content as string);
    setIsEditing(true);
  };

  const handleSave = () => {
    if (onEdit) {
      onEdit(messageIndex, editedContent);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedContent('');
  };

  const renderContent = () => {
    if (typeof message.content === 'string') {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {textToReactNodes(message.content)}
        </Typography>
      );
    }

    if (Array.isArray(message.content)) {
      return (
        <Box>
          {message.content.map((content: TicketMessageContent, index: number) => {
            switch (content.type) {
              case 'text':
                return content.text ? (
                  <Typography
                    key={`${index + 1}-${content.text}`}
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', mb: 1 }}
                  >
                    {textToReactNodes(content.text)}
                  </Typography>
                ) : null;

              case 'image_url':
                return content.image_url ? (
                  <Box key={`${index + 1}-${content.image_url.url}`} sx={{ mb: 1 }}>
                    <img
                      src={content.image_url.url}
                      alt="Imagen adjunta"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        borderRadius: '8px',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                ) : null;

              case 'document':
                return content.document ? (
                  <Paper
                    key={`${index + 1}-${content.document.url}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <FileIcon color="primary" />
                      <Box
                        sx={{
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                          }}
                        >
                          Documento
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          Formato: {content.document.format.toUpperCase()}
                        </Typography>
                      </Box>
                      <Link href={content.document.url} target="_blank" rel="noopener noreferrer">
                        Ver documento
                      </Link>
                    </Box>
                  </Paper>
                ) : null;

              case 'video':
                return content.video ? (
                  <Paper
                    key={`${index + 1}-${content.video.url}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 1, bgcolor: 'grey.50' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <PlayIcon color="primary" />
                      <Box
                        sx={{
                          flex: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'medium',
                          }}
                        >
                          Video
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {content.video.name || `Formato: ${content.video.format.toUpperCase()}`}
                        </Typography>
                      </Box>
                      <Link href={content.video.url} target="_blank" rel="noopener noreferrer">
                        Ver video
                      </Link>
                    </Box>
                  </Paper>
                ) : null;

              default:
                return null;
            }
          })}
        </Box>
      );
    }

    return null;
  };

  const { align, bgcolor } = TONE_STYLE[getMessageTone(message)];

  return (
    <MessageWrapper
      caption={getCaption()}
      align={align}
      bgcolor={bgcolor}
      date={message.createdAt}
      captionAction={
        canEdit && typeof message.content === 'string' && !isEditing ? (
          <IconButton
            size="small"
            onClick={handleEditMessage}
            color="primary"
            title="Editar mensaje"
            sx={{ padding: '2px' }}
          >
            <EditIcon sx={{ fontSize: '0.75rem' }} />
          </IconButton>
        ) : undefined
      }
    >
      {isEditing ? (
        <Box>
          <TextField
            multiline
            minRows={1}
            maxRows={20}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            placeholder="Edita el contenido del mensaje..."
            variant="outlined"
            sx={{
              mb: 1,
              width: '100%',
              '& .MuiInputBase-root': {
                width: '100%',
              },
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                lineHeight: 1.43,
                fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                padding: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                width: '100%',
              },
              '& fieldset': {
                borderColor: '#c4c4c4',
              },
            }}
            slotProps={{
              input: {
                sx: {
                  fontSize: '0.875rem',
                  lineHeight: 1.43,
                  padding: '2px 4px',
                  width: '100%',
                  boxSizing: 'border-box',
                },
              },
            }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <IconButton size="small" onClick={handleCancel} color="default" title="Cancelar">
              <CancelIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={handleSave} color="primary" title="Guardar">
              <SaveIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
      ) : (
        <Box>{renderContent()}</Box>
      )}
    </MessageWrapper>
  );
};

export default TicketMessageComponent;
